import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Tag, Typography, Spin, Alert, Button } from "antd";
import {
  LinkOutlined,
  AppstoreOutlined,
  BranchesOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { useMarketplaceApps } from "hooks/requests/useMarketplaceApps";
import { GalleryApp } from "./types";
import "./style.scss";

const { Title, Text } = Typography;

export type { GalleryApp, GalleryProfile } from "./types";

const PROFILE_TYPE_COLORS: Record<string, string> = {
  byoc: "#1890ff",
  data: "#52c41a",
  analytics: "#722ed1",
  dashboard: "#fa8c16",
};

// Format profile-type for display: remove underscores, special-case BYOC
const formatProfileTypeDisplay = (type: string): string => {
  if (type.toLowerCase() === "app_deployment_(byoc)") return "BYOC";
  return type.replace(/_/g, " ").toUpperCase();
};

// Only allow http/https URLs to be opened, guarding against schemes like javascript:
const isSafeUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

interface AppCardProps {
  app: GalleryApp;
  profileType: string;
  animationDelay: number;
  onUseAsTemplate?: (gitUrl: string, profileType: string) => void;
}

const AppCard: React.FC<AppCardProps> = memo(({ app, profileType, animationDelay, onUseAsTemplate }) => {
  const isValidUrl = isSafeUrl(app.url);
  const isValidGit = isSafeUrl(app.git);

  const handleView = useCallback(() => {
    if (isValidUrl) window.open(app.url, "_blank", "noopener,noreferrer");
  }, [app.url, isValidUrl]);

  const handleTemplate = useCallback(() => {
    if (isValidGit) onUseAsTemplate?.(app.git, profileType);
  }, [app.git, isValidGit, onUseAsTemplate, profileType]);

  const accentColor = PROFILE_TYPE_COLORS[profileType.toLowerCase()] ?? "#1890ff";
  const features = app.features ?? [];
  const capabilities = app["DSE Capabilities"] ?? [];
  // Static placeholders — replace with app.metrics fields once API provides them
  const metricStatus = app.metrics?.status ?? "running";
  const metricUsers = app.metrics?.users ?? null;
  const metricRating = app.metrics?.rating ?? null;

  return (
    <div
      className="gallery-card"
      style={{ animationDelay: `${animationDelay}ms` }}
      role="article"
      aria-label={app.name}
    >
      {/* ── Left: preview + title ── */}
      <div className="gallery-card__left">
        <div className="gallery-card__title-area">
          <Title level={5} className="gallery-card__name" title={app.name}>
            {app.name}
          </Title>
          <Tag
            className="gallery-card__badge"
            style={{ backgroundColor: accentColor, borderColor: accentColor }}
          >
            {formatProfileTypeDisplay(profileType)}
          </Tag>
        </div>
        <div className="gallery-card__preview">
          {app.image ? (
            <img
              src={app.image}
              alt={app.name}
              className="gallery-card__preview-img"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <PictureOutlined className="gallery-card__preview-icon" />
          )}
        </div>
      </div>

      {/* ── Center: features + metrics ── */}
      <div className="gallery-card__center">
        <div className="gallery-card__section">
          <Text className="gallery-card__section-heading">FEATURES</Text>
          {features.length > 0 ? (
            <ul className="gallery-card__list">
              {features.map((f) => <li key={f}>{f}</li>)}
            </ul>
          ) : (
            <span className="gallery-card__placeholder">- [ ... ]</span>
          )}
        </div>
        <div className="gallery-card__section">
          <Text className="gallery-card__section-heading">METRICS</Text>
          <ul className="gallery-card__list gallery-card__list--metrics">
            <li>{metricStatus}</li>
            <li>
              {metricUsers !== null && metricRating !== null
                ? `${metricUsers} users (${metricRating} stars)`
                : "— users (— stars)"}
            </li>
          </ul>
        </div>
      </div>

      {/* ── Right: DSE capabilities + actions ── */}
      <div className="gallery-card__right">
        <div className="gallery-card__section">
          <Text className="gallery-card__section-heading">DSE CAPABILITIES</Text>
          {capabilities.length > 0 ? (
            <ul className="gallery-card__list">
              {capabilities.map((c) => <li key={c}>{c}</li>)}
            </ul>
          ) : (
            <span className="gallery-card__placeholder">- [ ... ]</span>
          )}
        </div>
        <div className="gallery-card__actions">
          <button
            className="gallery-card__btn gallery-card__btn--view"
            type="button"
            onClick={handleView}
            disabled={!isValidUrl}
            title={isValidUrl ? undefined : "Invalid application URL"}
          >
            <LinkOutlined /> VIEW
          </button>
          <button
            className="gallery-card__btn gallery-card__btn--template"
            type="button"
            onClick={handleTemplate}
            disabled={!isValidGit}
            title={isValidGit ? undefined : "No template repository"}
          >
            <BranchesOutlined /> USE AS TEMPLATE
          </button>
        </div>
      </div>
    </div>
  );
});

AppCard.displayName = "AppCard";

const ALL_FILTER = "all";

interface AppGalleryProps {
  onUseAsTemplate?: (gitUrl: string, profileType: string) => void;
}

const AppGallery: React.FC<AppGalleryProps> = ({ onUseAsTemplate }) => {
  const [activeFilter, setActiveFilter] = useState<string>(ALL_FILTER);

  // Pass undefined for "All" so no profile_type param is sent to the API
  const apiFilter = activeFilter !== ALL_FILTER ? activeFilter : undefined;
  const { profiles, isLoading, error, refetch } = useMarketplaceApps(apiFilter);

  // Cache pill options from the first successful "All" fetch so pills stay visible while filtered
  const [cachedTypes, setCachedTypes] = useState<string[]>([]);
  useEffect(() => {
    if (!apiFilter && profiles.length > 0) {
      setCachedTypes(profiles.map((p) => p["profile-type"]));
    }
  }, [profiles, apiFilter]);

  const totalApps = useMemo(
    () => profiles.reduce((sum, p) => sum + p.apps.length, 0),
    [profiles],
  );

  return (
    <div className="gallery-page">
      <div className="gallery-page__topbar">
        <div className="gallery-page__heading">
          <AppstoreOutlined className="gallery-page__heading-icon" />
          <Title level={4} className="gallery-page__title">Application Gallery</Title>
        </div>
      </div>

      <div className="gallery-filter-bar">
        <button
          className={`gallery-filter-pill ${activeFilter === ALL_FILTER ? "gallery-filter-pill--active" : ""}`}
          onClick={() => setActiveFilter(ALL_FILTER)}
        >
          All
        </button>
        {cachedTypes.map((type) => (
          <button
            key={type}
            className={`gallery-filter-pill ${activeFilter === type ? "gallery-filter-pill--active" : ""}`}
            style={activeFilter === type ? { borderColor: PROFILE_TYPE_COLORS[type] ?? "#1890ff", backgroundColor: PROFILE_TYPE_COLORS[type] ?? "#1890ff" } : {}}
            onClick={() => setActiveFilter(type)}
          >
            {formatProfileTypeDisplay(type)}
          </button>
        ))}
      </div>

      <div className="gallery-content">
        {isLoading && (
          <div className="gallery-loading">
            <Spin size="large" />
          </div>
        )}
        {!isLoading && error && (
          <Alert
            type="error"
            message={error}
            action={<Button size="small" onClick={refetch}>Retry</Button>}
            className="gallery-error"
          />
        )}
        {!isLoading && !error && (
          <>
            <Text className="gallery-section__subtitle">
              {totalApps} application{totalApps !== 1 ? "s" : ""}
            </Text>
            {profiles.map((profile) => (
              <section key={profile["profile-type"]} className="gallery-section">
                <div className="gallery-grid">
                  {profile.apps.map((app, idx) => (
                    <AppCard
                      key={app.url}
                      app={app}
                      profileType={profile["profile-type"]}
                      animationDelay={idx * 80}
                      onUseAsTemplate={onUseAsTemplate}
                    />
                  ))}
                </div>
              </section>
            ))}
            {profiles.length === 0 && (
              <Text type="secondary" className="gallery-empty">No applications match the selected filter.</Text>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(AppGallery);
